import {ITipoDocumento, TipoDocumento} from './user.model';
import {IAnagraficaStatus} from "./apiresponses.models";
import { IApiStandardResponse } from './api/api.models';

/**
 * Interfaccia che rappresenta un array di comuni restituiti dall'API.
 * 
 * Estende Array per fornire metodi di ricerca e manipolazione sui dati dei comuni.
 * 
 * @remarks
 * I metodi `reduce` e `reduceRight` sono ereditati da Array e la loro documentazione
 * può essere consultata nella documentazione MDN di Array.
 */
export interface ISearchComune extends Array<I_API_GET_Comune> {
}

/**
 * Interfaccia che rappresenta i dati di un comune restituiti dall'API.
 * Contiene tutte le informazioni relative a un comune italiano, inclusi CAP, codici ISTAT,
 * province e altre informazioni amministrative.
 */
export interface I_API_GET_Comune {
  cap: string;
  capmultipli: string;
  cab_comune: string;
  cab_provincia: string;
  cap_max: string;
  cap_min: string;
  cessato: number;
  cin_comune: string;
  cin_provincia: string;
  cod_comune: string;
  cod_provincia_bi: string;
  cod_istat: string;
  codice: number;
  data_modifica: string;
  rev_data_update: number;
  descrizione: string;
  Descrizione: string;
  nuova_provincia: string;
  operatore: string;
  provincia: string;
}

/**
 * Interfaccia che rappresenta i dati di una nazione restituiti dall'API.
 * Contiene le informazioni relative a un paese, inclusi codici e descrizioni.
 */
export interface I_API_GET_Nazione {
  Descrizione: string;
  Codice: number;
  Country_code: string;
  descrizione: string;
  codice: number;
  country_code: string;
}

export interface IAPINazione {
  tipo: 'INazione';
  data: I_API_GET_Nazione[];
}

/**
 * Interfaccia base per un indirizzo generico.
 * Contiene le informazioni comuni a tutti i tipi di indirizzi.
 */
export interface IIndirizzo {
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: I_API_GET_Comune;
}

/**
 * Interfaccia che rappresenta un indirizzo di spedizione.
 * Estende le informazioni base di un indirizzo aggiungendo il nominativo.
 */
export interface I_IndirizzoSpedizione {
  nominativo: string;
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: I_API_GET_Comune;
}

/**
 * Interfaccia che rappresenta i dati di un indirizzo di spedizione per le chiamate API.
 */
export interface IApiIndirizzoSpedizione {
  nominativo: string;
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: string;
  provincia: string;
}

/**
 * Interfaccia che rappresenta la risposta API per la gestione degli indirizzi di spedizione.
 */
export interface IApiGetIndirizzoSpedizione extends IApiStandardResponse<IApiIndirizzoSpedizione> {}

/**
 * Interfaccia che rappresenta i dati per l'aggiornamento di un indirizzo di spedizione via API.
 */
export interface IApiPutIndirizzoSpedizione {
  nominativo: string;
  indirizzo: string;
  id_comune: number;
  cap: string;
}

export interface I_IndirizzoResidenza {
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: I_API_GET_Comune;
  nazione: I_API_GET_Nazione;
  id_nazione: number;
}

export interface I_API_GET_IndirizzoResidenza {
  indirizzo_residenza: string;
  id_comune_residenza: number;
  cap_residenza: string;
  comune_residenza: I_API_GET_Comune;
  nazione_residenza: I_API_GET_Nazione;
  id_nazione_residenza: number;
}

export interface I_API_PUT_IndirizzoResidenza {
  indirizzo_residenza: string;
  id_comune_residenza: number;
  cap_residenza: string;
  id_nazione_residenza: number;
}

export interface I_IndirizzoDomicilio {
  nominativo: string;
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: I_API_GET_Comune;
}

export interface I_API_GET_IndirizzoDomicilio {
  nominativo_domicilio: string;
  indirizzo_domicilio: string;
  id_comune_domicilio: number;
  cap_domicilio: string;
  comune_domicilio: I_API_GET_Comune;
}

export interface I_API_PUT_IndirizzoDomicilio {
  nominativo_domicilio: string;
  indirizzo_domicilio: string;
  id_comune_domicilio: number;
  cap_domicilio: string;
}

export interface I_StatoUtilizzo {
  id: number;
  descrizione: string;
  utilizzabile: boolean;
  modificabile: boolean;
  cancellato: boolean;
}

export type T_TipoIndirizzo = 'domicilio' | 'spedizione' | 'residenza';

export interface I_API_PUT_DatiBase {
  indirizzo: string;
  id_comune: number;
  cap: string;
  id_nazione: number;
}

export interface I_DatiBase {
  codice_fiscale: string;
  comune_costituzione: I_API_GET_Comune;
  data_costituzione: string;
  denominazione: string;
  id_anagrafica: number;
  id_comune_costituzione: number;
  id_natura_giuridica: number;
  id_nazione_costituzione: number;
  id_settore_aziendale: number;
  id_stato_utilizzo: number;
  last_check_stato_utilizzo: string;
  natura_giuridica: I_NaturaGiuridica;
  nazione_costituzione: I_API_GET_Nazione;
  partita_iva: string;
  registro_imprese: string;
  stato_utilizzo: I_StatoUtilizzo;
}

export interface I_API_PUT_SedeLegale {
  indirizzo: string;
  id_comune: number;
  cap: string;
  id_nazione: number;
}

export interface I_SedeLegale {
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: string;
  provincia: string;
  nazione: string;
  id_nazione: number;
  id_stato_utilizzo: number;
  stato_utilizzo: I_StatoUtilizzo;
  last_check_stato_utilizzo: any[];
}

export interface I_API_PUT_NaturaGiuridica {
  indirizzo: string;
  id_comune: number;
  cap: string;
  id_nazione: number;
}

export interface I_API_GET_NaturaGiuridica {
  id: number;
  cod_bi: string;
  descr_breve: string;
  descrizione: string;
  di_capitale: number;
}

export interface I_NaturaGiuridica {
  id: number;
  cod_bi: string;
  descr_breve: string;
  descrizione: string;
  di_capitale: number;
}

export interface I_API_PUT_SedeOperativa {
  indirizzo: string;
  id_comune: number;
  cap: string;
  nominativo: string;
}

export interface I_SedeOperativa {
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: I_API_GET_Comune;
  nominativo: string;
}

export interface I_API_PUT_LegaleRappresentante {
  cognome_legale_rappresentante: string;
  nome_legale_rappresentante: string;
  codice_fiscale_legale_rappresentante: string;
  id_tipo_documento_legale_rappresentante: number;
  numero_documento_legale_rappresentante: string;
  data_scadenza_documento_legale_rappresentante: string;
  data_rilascio_documento_legale_rappresentante: string;
  id_citta_rilascio_legale_rappresentante: number;
  email_legale_rappresentante: string;
  cellulare_legale_rappresentante: string;
}

export interface I_LegaleRappresentante {
  citta_nascita_legale_rappresentante: string;
  citta_rilascio_documento_legale_rappresentante: string;
  codice_fiscale_legale_rappresentante: string;
  cognome_legale_rappresentante: string;
  data_rilascio_documento_legale_rappresentante: string;
  data_nascita_legale_rappresentante: string;
  data_scadenza_documento_legale_rappresentante: string;
  id_citta_rilascio_documento_legale_rappresentante: number;
  id_tipo_documento_legale_rappresentante: number;
  legale_rappresentante: string;
  nome_legale_rappresentante: string;
  numero_documento_legale_rappresentante: string;
  provincia_nascita_legale_rappresentante: string;
  rilasciato_da_documento_legale_rappresentante: string;
  sesso_legale_rappresentante: string;
  tipo_documento_legale_rappresentante: ITipoDocumento;
  email_legale_rappresentante: string;
  cellulare_legale_rappresentante: string;
  stato_utilizzo: I_StatoUtilizzo;
}

export interface I_Contatti {
  primo_telefono_casa: string;
  secondo_telefono_ufficio: string;
  fax: string;
  cellulare: string;
  desc_altro_tel: string;
  altro_tel: string;
  email: string;
  pec: string;
  profilo_facebook: string;
}

export interface I_GET_Contatti extends I_Contatti, IAnagraficaStatus {
}

export interface I_ContattiReferente {
  referente: string;
  telefono_referente: string;
  email_referente: string;
  cellulare_referente: string;
}

export interface I_ConsensiPrivacy {
  trattamento_dati: boolean;
  dati_sensibili: boolean;
  attivita_commerciali: boolean;
  attivita_conto_terzi: boolean;
  data_privacy: string;
  rev_data_privacy: number;
}

export interface PUT_ConsensiPrivacy {
  trattamento_dati: boolean | null | undefined;
  dati_sensibili: boolean | null | undefined;
  attivita_commerciali: boolean | null | undefined;
  attivita_conto_terzi: boolean | null | undefined;
}

export interface I_DocumentoRiconoscimento {
  id_tipo_documento: number;
  tipo_documento: string;
  numero_documento: string;
  data_rilascio_documento: string;
  data_scadenza_documento: string;
  documento_rilasciato_da: string;
  id_citta_rilascio: number;
  comune_rilascio: I_API_GET_Comune;
}

/**
 * Classe che implementa l'interfaccia I_API_GET_Comune.
 * Fornisce un'implementazione concreta con valori di default e metodi di gestione.
 */
export class Comune implements I_API_GET_Comune {
  cap: string = '';
  capmultipli: string = '';
  cab_comune: string = '';
  cab_provincia: string = '';
  cap_max: string = '';
  cap_min: string = '';
  cessato: number = 0;
  cin_comune: string = '';
  cin_provincia: string = '';
  cod_comune: string = '';
  cod_provincia_bi: string = '';
  cod_istat: string = '';
  codice: number = 0;
  data_modifica: string = '';
  rev_data_update: number = 0;
  descrizione: string = '';
  Descrizione: string = '';
  nuova_provincia: string = '';
  operatore: string = '';
  provincia: string = '';

  /**
   * Costruttore della classe Comune.
   * @param data - Dati parziali per inizializzare il comune. Se non specificati, verranno utilizzati valori di default.
   */
  constructor(data: Partial<I_API_GET_Comune> = {}) {
    Object.assign(this, data);
  }
}

/**
 * Classe che implementa l'interfaccia I_API_GET_Nazione.
 * Fornisce un'implementazione concreta con valori di default e metodi di gestione.
 */
export class Nazione implements I_API_GET_Nazione {
  Descrizione: string = '';
  Codice: number = 0;
  Country_code: string = '';
  descrizione: string = '';
  codice: number = 0;
  country_code: string = '';

  /**
   * Costruttore della classe Nazione.
   * @param data - Dati parziali per inizializzare la nazione. Se non specificati, verranno utilizzati valori di default.
   */
  constructor(data: Partial<I_API_GET_Nazione> = {}) {
    Object.assign(this, data);
  }
}

/**
 * Classe che implementa l'interfaccia I_StatoUtilizzo.
 * Rappresenta lo stato di utilizzo di un'entità nel sistema.
 */
export class StatoUtilizzo implements I_StatoUtilizzo {
  id: number = 0;
  descrizione: string = '';
  utilizzabile: boolean = false;
  modificabile: boolean = false;
  cancellato: boolean = false;

  /**
   * Costruttore della classe StatoUtilizzo.
   * @param data - Dati parziali per inizializzare lo stato di utilizzo. Se non specificati, verranno utilizzati valori di default.
   */
  constructor(data: Partial<I_StatoUtilizzo> = {}) {
    Object.assign(this, data);
  }
}

/**
 * Classe che implementa l'interfaccia I_IndirizzoSpedizione.
 * Gestisce le informazioni relative a un indirizzo di spedizione.
 */
export class IndirizzoSpedizione implements I_IndirizzoSpedizione {
  nominativo: string;
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: I_API_GET_Comune;

  /**
   * Costruttore della classe IndirizzoSpedizione.
   * @param data - Dati parziali per inizializzare l'indirizzo di spedizione. Se non specificati, verranno utilizzati valori di default.
   */
  constructor({
    nominativo = '',
    indirizzo = '',
    id_comune = 0,
    cap = '',
    comune = new Comune(),
  }: Partial<I_IndirizzoSpedizione> = {}) {
    this.nominativo = nominativo;
    this.indirizzo = indirizzo;
    this.id_comune = id_comune;
    this.cap = cap;
    this.comune = comune;
  }

  /**
   * Converte l'oggetto in formato API per l'invio al backend.
   * @returns Oggetto formattato secondo l'interfaccia IApiPutIndirizzoSpedizione
   */
  toAPI(): IApiPutIndirizzoSpedizione {
    return {
      nominativo: this.nominativo,
      indirizzo: this.indirizzo,
      id_comune: this.id_comune,
      cap: this.cap,
    };
  }

  /**
   * Aggiorna i dati dell'indirizzo di spedizione con i nuovi valori forniti.
   * @param indirizzo_spedizione - Nuovi dati per l'indirizzo di spedizione
   */
  update(indirizzo_spedizione: I_IndirizzoSpedizione) {
    this.nominativo = indirizzo_spedizione.nominativo;
    this.indirizzo = indirizzo_spedizione.indirizzo;
    this.id_comune = indirizzo_spedizione.id_comune;
    this.cap = indirizzo_spedizione.cap;
    this.comune = new Comune(indirizzo_spedizione.comune);
  }
}

export class IndirizzoDomicilio implements I_IndirizzoDomicilio {
  nominativo: string;
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: I_API_GET_Comune;

  constructor({
    nominativo = '',
    indirizzo = '',
    id_comune = 0,
    cap = '',
    comune = new Comune(),
  }: Partial<I_IndirizzoDomicilio> = {}) {
    this.nominativo = nominativo;
    this.indirizzo = indirizzo;
    this.id_comune = id_comune;
    this.cap = cap;
    this.comune = comune;
  }

  toAPI(): I_API_PUT_IndirizzoDomicilio {
    return {
      nominativo_domicilio: this.nominativo,
      indirizzo_domicilio: this.indirizzo,
      id_comune_domicilio: this.id_comune,
      cap_domicilio: this.cap,
    };
  }

  update(indirizzo_domicilio: I_IndirizzoDomicilio) {
    this.nominativo = indirizzo_domicilio.nominativo;
    this.indirizzo = indirizzo_domicilio.indirizzo;
    this.id_comune = indirizzo_domicilio.id_comune;
    this.cap = indirizzo_domicilio.cap;
    this.comune = new Comune(indirizzo_domicilio.comune);
  }
}

export class IndirizzoResidenza implements I_IndirizzoResidenza {
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: I_API_GET_Comune;
  nazione: I_API_GET_Nazione;
  id_nazione: number;

  constructor({
    indirizzo = '',
    id_comune = 0,
    cap = '',
    comune = new Comune(),
    nazione = new Nazione(),
    id_nazione = 0,
  }: Partial<I_IndirizzoResidenza> = {}) {
    this.indirizzo = indirizzo;
    this.id_comune = id_comune;
    this.cap = cap;
    this.comune = comune;
    this.nazione = nazione;
    this.id_nazione = id_nazione;
  }

  toAPI(): I_API_PUT_IndirizzoResidenza {
    return {
      indirizzo_residenza: this.indirizzo,
      id_comune_residenza: this.id_comune,
      cap_residenza: this.cap,
      id_nazione_residenza: this.id_nazione,
    };
  }

  update(indirizzo_residenza: I_IndirizzoResidenza) {
    this.indirizzo = indirizzo_residenza.indirizzo;
    this.id_comune = indirizzo_residenza.id_comune;
    this.cap = indirizzo_residenza.cap;
    this.comune = new Comune(indirizzo_residenza.comune);
    this.nazione = new Nazione(indirizzo_residenza.nazione);
    this.id_nazione = indirizzo_residenza.id_nazione;
  }
}

/**
 * Classe che implementa l'interfaccia I_ConsensiPrivacy.
 * Gestisce i consensi privacy di un'entità.
 */
export class ConsensiPrivacy implements I_ConsensiPrivacy {
  trattamento_dati: boolean;
  dati_sensibili: boolean;
  attivita_commerciali: boolean;
  attivita_conto_terzi: boolean;
  data_privacy: string;
  rev_data_privacy: number;

  /**
   * Costruttore della classe ConsensiPrivacy.
   * @param data - Dati parziali per inizializzare i consensi privacy. Se non specificati, tutti i consensi saranno impostati a false.
   */
  constructor({
    trattamento_dati = false,
    dati_sensibili = false,
    attivita_commerciali = false,
    attivita_conto_terzi = false,
    data_privacy = '',
    rev_data_privacy = 0,
  }: Partial<I_ConsensiPrivacy> = {}) {
    this.trattamento_dati = trattamento_dati;
    this.dati_sensibili = dati_sensibili;
    this.attivita_commerciali = attivita_commerciali;
    this.attivita_conto_terzi = attivita_conto_terzi;
    this.data_privacy = data_privacy;
    this.rev_data_privacy = rev_data_privacy;
  }

  /**
   * Converte l'oggetto in formato API per l'invio al backend.
   * @returns Oggetto formattato secondo l'interfaccia PUT_ConsensiPrivacy
   */
  toAPI(): PUT_ConsensiPrivacy {
    return {
      trattamento_dati: this.trattamento_dati,
      dati_sensibili: this.dati_sensibili,
      attivita_commerciali: this.attivita_commerciali,
      attivita_conto_terzi: this.attivita_conto_terzi,
    };
  }

  /**
   * Aggiorna i consensi privacy con i nuovi valori forniti.
   * @param consensi - Nuovi dati per i consensi privacy
   */
  update(consensi: I_ConsensiPrivacy) {
    this.trattamento_dati = consensi.trattamento_dati;
    this.dati_sensibili = consensi.dati_sensibili;
    this.attivita_commerciali = consensi.attivita_commerciali;
    this.attivita_conto_terzi = consensi.attivita_conto_terzi;
    this.data_privacy = consensi.data_privacy;
    this.rev_data_privacy = consensi.rev_data_privacy;
  }
}

/**
 * Classe che implementa l'interfaccia I_Contatti.
 * Gestisce le informazioni di contatto di un'entità.
 */
export class Contatti implements I_Contatti {
  primo_telefono_casa: string;
  secondo_telefono_ufficio: string;
  fax: string;
  cellulare: string;
  desc_altro_tel: string;
  altro_tel: string;
  email: string;
  pec: string;
  profilo_facebook: string;

  /**
   * Costruttore della classe Contatti.
   * @param data - Dati parziali per inizializzare i contatti. I numeri di telefono verranno automaticamente formattati rimuovendo il prefisso +39.
   */
  constructor({
    primo_telefono_casa = '',
    secondo_telefono_ufficio = '',
    fax = '',
    cellulare = '',
    desc_altro_tel = '',
    altro_tel = '',
    email = '',
    pec = '',
    profilo_facebook = '',
  }: Partial<I_Contatti> = {}) {
    this.primo_telefono_casa = primo_telefono_casa.replace('+39', '');
    this.secondo_telefono_ufficio = secondo_telefono_ufficio.replace('+39', '');
    this.fax = fax.replace('+39', '');
    this.cellulare = cellulare.replace('+39', '');
    this.desc_altro_tel = desc_altro_tel;
    this.altro_tel = altro_tel;
    this.email = email;
    this.pec = pec;
    this.profilo_facebook = profilo_facebook;
  }

  /**
   * Aggiorna i contatti con i nuovi valori forniti.
   * @param contatti - Nuovi dati per i contatti
   */
  update(contatti: I_Contatti) {
    this.primo_telefono_casa = contatti.primo_telefono_casa;
    this.secondo_telefono_ufficio = contatti.secondo_telefono_ufficio;
    this.fax = contatti.fax;
    this.cellulare = contatti.cellulare;
    this.desc_altro_tel = contatti.desc_altro_tel;
    this.altro_tel = contatti.altro_tel;
    this.email = contatti.email;
    this.pec = contatti.pec;
    this.profilo_facebook = contatti.profilo_facebook;
  }
}

export class ContattiReferente implements I_ContattiReferente {
  referente: string;
  telefono_referente: string;
  email_referente: string;
  cellulare_referente: string;

  constructor({
    referente = '',
    telefono_referente = '',
    email_referente = '',
    cellulare_referente = '',
  }: Partial<I_ContattiReferente> = {}) {
    this.referente = referente;
    this.telefono_referente = telefono_referente;
    this.email_referente = email_referente;
    this.cellulare_referente = cellulare_referente;
  }

  update(contatti: I_ContattiReferente) {
    this.referente = contatti.referente;
    this.telefono_referente = contatti.telefono_referente;
    this.email_referente = contatti.email_referente;
    this.cellulare_referente = contatti.cellulare_referente;
  }

  updateToForm(data: any) {
    this.referente = data.referente;
    this.telefono_referente = data.telefono_referente;
    this.email_referente = data.email_referente;
    this.cellulare_referente = data.cellulare_referente;
  }
}

/**
 * Classe che implementa l'interfaccia I_LegaleRappresentante.
 * Gestisce le informazioni relative al legale rappresentante di un'entità.
 */
export class LegaleRappresentante implements I_LegaleRappresentante {
  citta_nascita_legale_rappresentante: string = '';
  citta_rilascio_documento_legale_rappresentante: string = '';
  codice_fiscale_legale_rappresentante: string = '';
  cognome_legale_rappresentante: string = '';
  data_rilascio_documento_legale_rappresentante: string = '';
  data_nascita_legale_rappresentante: string = '';
  data_scadenza_documento_legale_rappresentante: string = '';
  id_citta_rilascio_documento_legale_rappresentante: number = 0;
  id_tipo_documento_legale_rappresentante: number = 0;
  legale_rappresentante: string = '';
  nome_legale_rappresentante: string = '';
  numero_documento_legale_rappresentante: string = '';
  provincia_nascita_legale_rappresentante: string = '';
  rilasciato_da_documento_legale_rappresentante: string = '';
  sesso_legale_rappresentante: string = '';
  tipo_documento_legale_rappresentante: ITipoDocumento = new TipoDocumento();
  email_legale_rappresentante: string = '';
  cellulare_legale_rappresentante: string = '';
  stato_utilizzo: I_StatoUtilizzo = new StatoUtilizzo();

  /**
   * Costruttore della classe LegaleRappresentante.
   * @param data - Dati parziali per inizializzare il legale rappresentante. Se non specificati, verranno utilizzati valori di default.
   */
  constructor(data: Partial<I_LegaleRappresentante> = {}) {
    Object.assign(this, data);
  }

  /**
   * Converte l'oggetto in formato API per l'invio al backend.
   * @returns Oggetto formattato secondo l'interfaccia I_API_PUT_LegaleRappresentante
   */
  toAPI(): I_API_PUT_LegaleRappresentante {
    return {
      cognome_legale_rappresentante: this.cognome_legale_rappresentante,
      nome_legale_rappresentante: this.nome_legale_rappresentante,
      codice_fiscale_legale_rappresentante: this.codice_fiscale_legale_rappresentante,
      id_tipo_documento_legale_rappresentante: this.id_tipo_documento_legale_rappresentante,
      numero_documento_legale_rappresentante: this.numero_documento_legale_rappresentante,
      data_rilascio_documento_legale_rappresentante: this.data_rilascio_documento_legale_rappresentante,
      data_scadenza_documento_legale_rappresentante: this.data_scadenza_documento_legale_rappresentante,
      id_citta_rilascio_legale_rappresentante: this.id_citta_rilascio_documento_legale_rappresentante,
      email_legale_rappresentante: this.email_legale_rappresentante,
      cellulare_legale_rappresentante: this.cellulare_legale_rappresentante,
    };
  }

  /**
   * Aggiorna i dati del legale rappresentante con i nuovi valori forniti.
   * @param legale - Nuovi dati per il legale rappresentante
   */
  update(legale: I_LegaleRappresentante) {
    Object.assign(this, legale);
  }

  /**
   * Aggiorna i dati del legale rappresentante con i valori del form.
   * @param data - Dati provenienti dal form
   */
  updateToForm(data: any) {
    this.cognome_legale_rappresentante = data.cognome_legale_rappresentante;
    this.nome_legale_rappresentante = data.nome_legale_rappresentante;
    this.codice_fiscale_legale_rappresentante = data.codice_fiscale_legale_rappresentante;
    this.id_tipo_documento_legale_rappresentante = data.id_tipo_documento_legale_rappresentante;
    this.numero_documento_legale_rappresentante = data.numero_documento_legale_rappresentante;
    this.data_rilascio_documento_legale_rappresentante = data.data_rilascio_documento_legale_rappresentante;
    this.data_scadenza_documento_legale_rappresentante = data.data_scadenza_documento_legale_rappresentante;
    this.id_citta_rilascio_documento_legale_rappresentante = data.id_citta_rilascio_documento_legale_rappresentante;
    this.email_legale_rappresentante = data.email_legale_rappresentante;
    this.cellulare_legale_rappresentante = data.cellulare_legale_rappresentante;
  }
}

export class NaturaGiuridica implements I_NaturaGiuridica {
  id: number;
  cod_bi: string;
  descr_breve: string;
  descrizione: string;
  di_capitale: number;

  constructor({
    id = 0,
    cod_bi = '',
    descr_breve = '',
    descrizione = '',
    di_capitale = 0,
  }: Partial<I_NaturaGiuridica> = {}) {
    this.id = id;
    this.cod_bi = cod_bi;
    this.descr_breve = descr_breve;
    this.descrizione = descrizione;
    this.di_capitale = di_capitale;
  }

  update(natura: I_NaturaGiuridica) {
    Object.assign(this, natura);
  }
}

/**
 * Classe che implementa l'interfaccia I_SedeLegale.
 * Gestisce le informazioni relative alla sede legale di un'entità.
 */
export class SedeLegale implements I_SedeLegale {
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: string;
  provincia: string;
  nazione: string;
  id_nazione: number;
  id_stato_utilizzo: number;
  stato_utilizzo: I_StatoUtilizzo;
  last_check_stato_utilizzo: any[];

  constructor({
    indirizzo = '',
    id_comune = 0,
    cap = '',
    comune = '',
    provincia = '',
    nazione = '',
    id_nazione = 0,
    id_stato_utilizzo = 0,
    stato_utilizzo = new StatoUtilizzo(),
    last_check_stato_utilizzo = [],
  }: Partial<I_SedeLegale> = {}) {
    this.indirizzo = indirizzo;
    this.id_comune = id_comune;
    this.cap = cap;
    this.comune = comune;
    this.provincia = provincia;
    this.nazione = nazione;
    this.id_nazione = id_nazione;
    this.id_stato_utilizzo = id_stato_utilizzo;
    this.stato_utilizzo = stato_utilizzo;
    this.last_check_stato_utilizzo = last_check_stato_utilizzo;
  }

  update(sede: I_SedeLegale) {
    Object.assign(this, sede);
  }

  updateToForm(data: any) {
    this.indirizzo = data.indirizzo;
    this.id_comune = data.id_comune;
    this.cap = data.cap;
    this.comune = data.comune;
    this.provincia = data.provincia;
    this.nazione = data.nazione;
    this.id_nazione = data.id_nazione;
  }
}

/**
 * Interfaccia che rappresenta la risposta API per la gestione della sede legale.
 */
export interface IApiSedeLegaleResponse extends IApiStandardResponse<I_SedeLegale> {}

/**
 * Interfaccia che rappresenta un comune per la ricerca.
 * Contiene le informazioni essenziali per la ricerca comuni.
 */
export interface IComuneSearch {
  codice: number;
  descrizione: string;
  provincia: string;
  cessato: number;
  cap: string;
  cap_multipli: string;
  cap_min: string;
  cap_max: string;
  cab_comune: string;
  cin_comune: string;
  cab_provincia: string;
  cin_provincia: string;
  cod_provincia_bi: string;
  cod_comune: string;
  cod_istat: string;
}

/**
 * Interfaccia per la risposta API della ricerca comuni.
 * Estende l'interfaccia standard di risposta API.
 */
export interface IApiComuniSearchResponse extends IApiStandardResponse<IComuneSearch[]> {}

/**
 * Classe che implementa l'interfaccia I_SedeOperativa.
 * Gestisce le informazioni relative alla sede operativa di un'entità.
 */
export class SedeOperativa implements I_SedeOperativa {
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: I_API_GET_Comune;
  nominativo: string;

  /**
   * Costruttore della classe SedeOperativa.
   * @param data - Dati parziali per inizializzare la sede operativa. Se non specificati, verranno utilizzati valori di default.
   */
  constructor({
    indirizzo = '',
    id_comune = 0,
    cap = '',
    comune = new Comune(),
    nominativo = '',
  }: Partial<I_SedeOperativa> = {}) {
    this.indirizzo = indirizzo;
    this.id_comune = id_comune;
    this.cap = cap;
    this.comune = comune;
    this.nominativo = nominativo;
  }

  /**
   * Converte l'oggetto in formato API per l'invio al backend.
   * @returns Oggetto formattato secondo l'interfaccia I_API_PUT_SedeOperativa
   */
  toAPI(): I_API_PUT_SedeOperativa {
    return {
      indirizzo: this.indirizzo,
      id_comune: this.id_comune,
      cap: this.cap,
      nominativo: this.nominativo,
    };
  }

  /**
   * Aggiorna i dati della sede operativa con i nuovi valori forniti.
   * @param sede - Nuovi dati per la sede operativa
   */
  update(sede: I_SedeOperativa) {
    Object.assign(this, sede);
  }

  /**
   * Aggiorna i dati della sede operativa con i valori dell'API.
   * @param data - Dati provenienti dall'API
   */
  updateToAPI(data: any) {
    this.indirizzo = data.indirizzo;
    this.id_comune = data.id_comune;
    this.cap = data.cap;
    this.comune = data.comune;
    this.nominativo = data.nominativo;
  }

  /**
   * Aggiorna i dati della sede operativa con i valori del form.
   * @param data - Dati provenienti dal form
   */
  updateToForm(data: any) {
    this.indirizzo = data.indirizzo;
    this.id_comune = data.id_comune;
    this.cap = data.cap;
    this.comune = data.comune;
    this.nominativo = data.nominativo;
  }
}

/**
 * Classe che implementa l'interfaccia I_DatiBase.
 * Gestisce le informazioni base di un'entità.
 */
export class DatiBase implements I_DatiBase {
  codice_fiscale: string = '';
  comune_costituzione: I_API_GET_Comune = new Comune();
  data_costituzione: string = '';
  denominazione: string = '';
  id_anagrafica: number = 0;
  id_comune_costituzione: number = 0;
  id_natura_giuridica: number = 0;
  id_nazione_costituzione: number = 0;
  id_settore_aziendale: number = 0;
  id_stato_utilizzo: number = 0;
  last_check_stato_utilizzo: string = '';
  natura_giuridica: I_NaturaGiuridica = new NaturaGiuridica();
  nazione_costituzione: I_API_GET_Nazione = new Nazione();
  partita_iva: string = '';
  registro_imprese: string = '';
  stato_utilizzo: I_StatoUtilizzo = new StatoUtilizzo();

  /**
   * Costruttore della classe DatiBase.
   * @param data - Dati parziali per inizializzare i dati base. Se non specificati, verranno utilizzati valori di default.
   */
  constructor(data: Partial<I_DatiBase> = {}) {
    Object.assign(this, data);
  }
}

/**
 * Classe che implementa l'interfaccia I_DocumentoRiconoscimento.
 * Gestisce le informazioni relative a un documento di riconoscimento.
 */
export class DocumentoRiconoscimento implements I_DocumentoRiconoscimento {
  id_tipo_documento: number = 0;
  tipo_documento: string = '';
  numero_documento: string = '';
  data_rilascio_documento: string = '';
  data_scadenza_documento: string = '';
  documento_rilasciato_da: string = '';
  id_citta_rilascio: number = 0;
  comune_rilascio: I_API_GET_Comune = new Comune();

  /**
   * Costruttore della classe DocumentoRiconoscimento.
   * @param data - Dati parziali per inizializzare il documento di riconoscimento. Se non specificati, verranno utilizzati valori di default.
   */
  constructor(data: Partial<I_DocumentoRiconoscimento> = {}) {
    Object.assign(this, data);
  }
}

export interface I_SedeOperativaApi {
  nominativo: string;
  indirizzo: string;
  id_comune: number;
  cap: string;
  comune: any; // può essere null
  provincia: any; // può essere null
  id_stato_utilizzo: number;
  stato_utilizzo: I_StatoUtilizzo;
  last_check_stato_utilizzo: any[];
}

/**
 * Interfaccia che rappresenta la risposta API per la gestione della sede operativa.
 */
export interface IApiSedeOperativaResponse extends IApiStandardResponse<I_SedeOperativaApi> {}

/**
 * Interfaccia che rappresenta la risposta API per la gestione del legale rappresentante.
 */
export interface IApiLegaleRappresentanteResponse extends IApiStandardResponse<I_LegaleRappresentante> {}

// Interface for privacy response data
export interface IPrivacyResponse {
  trattamento_dati: boolean;
  dati_sensibili: boolean;
  attivita_commerciali: boolean;
  attivita_conto_terzi: boolean;
  data_privacy: string;
  operatore_verifica: string;
  id_stato_utilizzo: number;
  stato_utilizzo: I_StatoUtilizzo;
  last_check_stato_utilizzo: string;
}

// Interface for the complete API privacy response
export interface IApiPrivacyResponse extends IApiStandardResponse<IPrivacyResponse> {}
